"use client"

import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react"
import { Fragment, useEffect, useMemo, useState } from "react"
import ReactCountryFlag from "react-country-flag"
import { useTranslations } from "next-intl"
import { MapPin, ChevronDown } from "lucide-react"

import { useParams, usePathname } from "next/navigation"
import { updateRegion } from "@lib/data/cart"
import { HttpTypes } from "@medusajs/types"
import { Button } from "@/components/common"

type CountryOption = {
  country: string
  region: string
  label: string
}

type CountrySelectorProps = {
  regions: HttpTypes.StoreRegion[]
}

const CountrySelector = ({ regions }: CountrySelectorProps) => {
  const t = useTranslations("common.country")
  const [current, setCurrent] = useState<CountryOption | null>(null)

  const { countryCode } = useParams()
  const currentPath = usePathname().split(`/${countryCode}`)[1]

  const options = useMemo(() => {
    return regions
      ?.map((r) => {
        return r.countries?.map((c) => ({
          country: c.iso_2,
          region: r.id,
          label: c.display_name,
        }))
      })
      .flat()
      .filter((o): o is CountryOption => o !== undefined)
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [regions])

  useEffect(() => {
    if (countryCode && options) {
      const option = options.find((o) => o.country === countryCode)
      if (option) {
        setCurrent(option)
      }
    }
  }, [options, countryCode])

  const handleChange = (option: CountryOption, close: () => void) => {
    updateRegion(option.country, currentPath)
    close()
  }

  if (!regions || regions.length === 0) {
    return null
  }

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <PopoverButton className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground-secondary hover:text-foreground rounded-lg hover:bg-surface-subtle transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring">
            <MapPin className="h-4 w-4" />
            {current ? (
              <span className="flex items-center gap-2">
                <ReactCountryFlag
                  svg
                  style={{ width: "16px", height: "16px" }}
                  countryCode={current.country}
                />
                <span className="hidden lg:inline">{current.label}</span>
              </span>
            ) : (
              <span>{t("shippingTo")}</span>
            )}
            <ChevronDown
              className={`h-4 w-4 transition-transform duration-200 ${
                open ? "rotate-180" : ""
              }`}
            />
          </PopoverButton>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <PopoverPanel className="absolute right-0 z-50 mt-2 w-64 origin-top-right rounded-xl bg-popover shadow-lg ring-1 ring-border focus:outline-hidden">
              <div className="p-2">
                <div className="px-3 py-2 text-xs font-medium text-foreground-tertiary uppercase tracking-wider">
                  {t("shippingTo")}
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {options?.map((option) => (
                    <Button
                      variant="ghost"
                      key={option.country}
                      onClick={() => handleChange(option, close)}
                      className={`h-auto p-0 rounded-none font-normal w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors ${
                        current?.country === option.country
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-foreground hover:bg-surface-subtle"
                      }`}
                    >
                      <ReactCountryFlag
                        svg
                        style={{ width: "20px", height: "20px" }}
                        countryCode={option.country}
                      />
                      {option.label}
                    </Button>
                  ))}
                </div>
              </div>
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  )
}

export default CountrySelector
